import React from "react";
import * as shapes from "@wbly/tokens/shape";

import * as styles from "./Shape.module.css";

export type ShapeProps = {
  shape: keyof typeof shapes;
  manual?: boolean;
} & JSX.IntrinsicElements["svg"];

/**
 * Generic component for inserting brand shapes as background elements.
 *
 * Accepts any of the brand shape elements as a shape prop, which will then render as an inline svg via the use element.
 *
 * Either supports a custom CSS props based positioning API, or manual positioning for edge cases.
 *
 * Automatically uses the palette accent colour for support with the `[data-palette]` utility. Colour can be overridden using the `--shape-color` property.
 *
 * The shape maintains aspect ratio using `aspect-ratio: 1/1` while in automatic positioning mode.
 *
 * Supports manual positioning using custom CSS and `[data-manual="true"]`.
 *
 * **Custom CSS Vars**
 *
 * | prop | description | values |
 * |--|--|--|
 * | `--shape-size` | the size of the shape, applied to width. Percentages will be relative to the width. Element maintains static aspect ratio. | `<length>`, `<percentage>` |
 * | `--shape-anchor-x` | internal anchor point, where 0 is the left edge and 1 is the right edge. Calculated via negative margin-left, multiplying by --shape-size. | `number (0-1)` |
 * | `--shape-anchor-y` | internal anchor point, where 0 is the top edge and 1 is the bottom edge. Calculated via negative margin-top, multiplying by --shape-size. | `number (0-1)` |
 * | `--shape-position-x` | the position of the shape. Applied directly to `left`. | `<length>`, `<percentage>` |
 * | `--shape-position-y` | the position of the shape. Applied directly to `top`. | `<length>`, `<percentage>` |
 * | `--shape-color` | the colour of the shape. Applied directly to `fill`. Defaults to `--palette-accent-colour`. | `<color>` |
 *
 * **Example**
 *
 * ```jsx
 * <Shape className="Example-shape" shape="scallop" />
 * ```
 * ```css
 * .Example-shape {
 *  --shape-anchor-y: 0.75;
 *  --shape-anchor-x: 0.5;
 *  --shape-position-y: -10px;
 *  --shape-position-x: 50%;
 *  --shape-size: 120%;
 * }
 * ```
 */
export function Shape({
  shape,
  manual = false,
  className = "",
  ...props
}: ShapeProps) {
  return (
    <svg
      className={`${styles["Shape"]} ${className}`}
      viewBox="0 0 262 262"
      role="presentation"
      data-manual={manual}
      {...props}
    >
      <use href={`${shapes[shape]}#shape`} />
    </svg>
  );
}

Shape.displayName = "Shape";
